import {
  hitRatePercent,
  tierName,
  type League,
  type Membership
} from '@h2h/shared';
import { TierDot } from '../DivisionStrip';
import { DivisionsHelp } from '../DivisionsHelpSheet';
import { TierLadder } from '../../rounds/RoundResultsHero';
import { Placeholder } from '../../ui/Placeholder';
import { Trophy } from '../../ui/icons';
import { cap, formatCount, signedPoints } from '../../../lib/format';
import { type SeasonRoundResult } from '../../../hooks/useQueries';
import { RoundHistoryList } from './RoundHistoryList';

export function SeasonStandingsView({
  league,
  membership,
  results,
  totalRounds,
  onOpen
}: {
  league: League;
  membership: Membership;
  results: SeasonRoundResult[];
  totalRounds: number;
  /** Open a settled round's full recap (incl. its final division table). When set,
   *  each history row becomes tappable — so every past division is viewable, not
   *  just the most recent one. */
  onOpen?: (roundId: string) => void;
}) {
  const seasonPoints = results.reduce((sum, r) => sum + (r.entry.roundScore ?? 0), 0);
  const promotions = results.filter((r) => r.entry.movement === 'PROMOTED').length;
  const relegations = results.filter((r) => r.entry.movement === 'RELEGATED').length;
  const tiersGained = promotions - relegations;
  const bestRound = results.reduce((max, r) => Math.max(max, r.entry.roundScore ?? 0), 0);
  const hitRate = hitRatePercent(membership);
  const progress = totalRounds > 0 ? Math.round((results.length / totalRounds) * 100) : 0;

  return (
    <>
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1C1C24] to-[#0C0C10] p-[22px] shadow-[0_22px_42px_-18px_rgba(0,0,0,0.55)]">
        <span className="pointer-events-none absolute -right-[50px] -top-[60px] h-[200px] w-[200px] rounded-full bg-[radial-gradient(circle,rgba(227,168,43,0.22),transparent_70%)]" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wide text-[#9A9AA4]">Season points</span>
            <span className="inline-flex items-center gap-[6px] rounded-full border border-[#E3A82B]/35 bg-[#E3A82B]/15 px-[11px] py-[5px]">
              <TierDot tierIndex={membership.tier} />
              <span className="text-[11px] font-extrabold tracking-wide text-[#E8C25E]">
                {cap(tierName(membership.tier)).toUpperCase()} TIER
              </span>
            </span>
          </div>
          <div className="num mt-3 text-[48px] font-extrabold leading-none tracking-title text-white">
            {formatCount(seasonPoints)}
          </div>
          <div className="mt-[18px] flex items-center gap-[10px]">
            <div className="h-[6px] flex-1 overflow-hidden rounded-[3px] bg-white/[0.14]">
              <div
                className="h-full rounded-[3px] bg-gradient-to-r from-accent to-[#1E9E57]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="num whitespace-nowrap text-[11px] font-bold text-[#9A9AA4]">
              {results.length} / {totalRounds} rounds
            </span>
          </div>
        </div>
      </div>

      <TierLadder currentTierIndex={membership.tier} tiersGained={tiersGained} label="Tier journey" />

      <div className="-mt-1 flex justify-center">
        <DivisionsHelp tierIndex={membership.tier} variant="inline" label="How promotion works" />
      </div>

      <div className="grid grid-cols-3 gap-[10px]">
        {[
          { value: String(promotions), label: 'Promotions' },
          { value: bestRound > 0 ? signedPoints(bestRound) : '—', label: 'Best round' },
          { value: hitRate != null ? `${hitRate}%` : '—', label: 'Hit rate' }
        ].map(({ value, label }) => (
          <div key={label} className="rounded-[18px] border border-black/5 bg-card p-[14px] shadow-tile">
            <div className="num text-[24px] font-extrabold leading-none tracking-tight text-ink">{value}</div>
            <div className="mt-[6px] text-[10px] font-semibold leading-tight text-ink2">{label}</div>
          </div>
        ))}
      </div>

      {results.length === 0 ? (
        <Placeholder
          Icon={Trophy}
          title="No settled rounds yet"
          body={`Play a round in ${league.name} and your results, promotions and tier moves land here.`}
        />
      ) : (
        <RoundHistoryList results={results} onOpen={onOpen} />
      )}
    </>
  );
}
